import React, { useEffect, useMemo, useState } from "react";
import { Search, Plus } from "lucide-react";
import "./scheduling.css";
import { supabase } from "../../Services/supabaseClient";
import { useCompany } from "../../context/CompanyContext";
import BookingModal from "../../components/scheduling/BookingModal";
import Toast from "../../components/scheduling/Toast";
import { SkeletonRow } from "../../components/scheduling/Skeleton";

function toIso(dtLocal) {
  return new Date(dtLocal).toISOString();
}

export default function Finder() {
  const { companyId } = useCompany();

  const [spaces, setSpaces] = useState([]);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");

  const [query, setQuery] = useState({
    date: "",
    start: "09:00",
    end: "10:00",
    capacity: "",
    tag: "",
  });

  const [results, setResults] = useState([]);
  const [finding, setFinding] = useState(false);

  const [toast, setToast] = useState({
    open: false,
    type: "info",
    message: "",
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSpaceId, setSelectedSpaceId] = useState("");

  const activeSpaces = useMemo(
    () => spaces.filter((s) => s.status === "active"),
    [spaces]
  );

  async function loadSpaces() {
    if (!companyId) return;
    setBusy(true);
    setErr("");
    try {
      const { data, error } = await supabase
        .from("spaces")
        .select("*")
        .eq("company_id", companyId)
        .order("name");

      if (error) throw error;
      setSpaces(data || []);
    } catch (e) {
      setErr(e?.message || "Failed to load spaces");
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    loadSpaces();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  function buildWindow() {
    if (!query.date) return null;
    const startLocal = `${query.date}T${query.start}`;
    const endLocal = `${query.date}T${query.end}`;
    const start = new Date(startLocal);
    const end = new Date(endLocal);
    if (!(end > start)) return null;
    return {
      startLocal,
      endLocal,
      startIso: toIso(startLocal),
      endIso: toIso(endLocal),
    };
  }

  async function find() {
    const w = buildWindow();
    if (!w) {
      setToast({
        open: true,
        type: "error",
        message: "Invalid date/time range",
      });
      return;
    }

    setFinding(true);
    try {
      // fetch bookings that could overlap; buffer overlaps are enforced server-side,
      // but for finder we do a conservative filter on raw times (still safe).
      const { data: bookings, error: bErr } = await supabase
        .from("space_bookings")
        .select("space_id,start_at,end_at,status")
        .eq("company_id", companyId)
        .eq("status", "confirmed")
        .lt("start_at", w.endIso)
        .gt("end_at", w.startIso);

      if (bErr) throw bErr;

      const taken = new Set((bookings || []).map((b) => b.space_id));

      let filtered = activeSpaces;

      if (query.capacity) {
        const cap = Number(query.capacity);
        filtered = filtered.filter((s) => s.capacity >= cap);
      }

      if (query.tag) {
        const t = query.tag.trim().toLowerCase();
        filtered = filtered.filter((s) =>
          (s.tags || []).some((x) => String(x).toLowerCase() === t)
        );
      }

      filtered = filtered.filter((s) => !taken.has(s.id));

      setResults(filtered);
      setToast({
        open: true,
        type: "success",
        message: `Found ${filtered.length} available space(s)`,
      });
    } catch (e) {
      setToast({
        open: true,
        type: "error",
        message: e?.message || "Finder failed",
      });
    } finally {
      setFinding(false);
    }
  }

  const w = buildWindow();

  return (
    <div className="sched-card">
      <div className="sched-card-head">
        <div>
          <h3 className="sched-h3">Availability Finder</h3>
          <p className="sched-muted">
            Pick a time window; we show active spaces that are not booked.
          </p>
        </div>
      </div>

      {busy ? (
        <SkeletonRow cols={4} />
      ) : err ? (
        <div className="sched-error">{err}</div>
      ) : (
        <>
          <div className="sched-finder">
            <div className="sched-filter">
              <label>Date</label>
              <input
                type="date"
                value={query.date}
                onChange={(e) =>
                  setQuery((p) => ({ ...p, date: e.target.value }))
                }
              />
            </div>
            <div className="sched-filter">
              <label>Start</label>
              <input
                type="time"
                value={query.start}
                onChange={(e) =>
                  setQuery((p) => ({ ...p, start: e.target.value }))
                }
              />
            </div>
            <div className="sched-filter">
              <label>End</label>
              <input
                type="time"
                value={query.end}
                onChange={(e) =>
                  setQuery((p) => ({ ...p, end: e.target.value }))
                }
              />
            </div>
            <div className="sched-filter">
              <label>Capacity (optional)</label>
              <input
                type="number"
                value={query.capacity}
                onChange={(e) =>
                  setQuery((p) => ({ ...p, capacity: e.target.value }))
                }
                placeholder="e.g. 8"
              />
            </div>
            <div className="sched-filter">
              <label>Tag (optional)</label>
              <input
                value={query.tag}
                onChange={(e) =>
                  setQuery((p) => ({ ...p, tag: e.target.value }))
                }
                placeholder="e.g. projector"
              />
            </div>
            <button
              className="sched-btn primary"
              onClick={find}
              disabled={finding}
            >
              <Search size={16} /> {finding ? "Finding..." : "Find"}
            </button>
          </div>

          <div className="sched-divider" />

          {results.length === 0 ? (
            <div className="sched-empty">
              <div className="sched-empty-title">No available spaces yet</div>
              <div className="sched-muted">
                Run the finder to see availability.
              </div>
            </div>
          ) : (
            <div className="sched-grid-cards">
              {results.map((s) => (
                <div key={s.id} className="sched-space">
                  <div className="sched-space-top">
                    <div>
                      <div className="sched-space-name">{s.name}</div>
                      <div className="sched-space-sub">
                        {s.location_building || "—"} • {s.location_floor || "—"}{" "}
                        • {s.location_section || "—"}
                      </div>
                    </div>
                    <div className="sched-pill ok">Available</div>
                  </div>

                  <div className="sched-space-meta">
                    <div>
                      <b>Capacity:</b> {s.capacity}
                    </div>
                    <div>
                      <b>Buffer:</b> {s.buffer_before_min}m /{" "}
                      {s.buffer_after_min}m
                    </div>
                    <div>
                      <b>Min/Max:</b> {s.min_duration_min}m /{" "}
                      {s.max_duration_min}m
                    </div>
                  </div>

                  <div className="sched-space-actions">
                    <button
                      className="sched-btn primary"
                      onClick={() => {
                        if (!w) {
                          setToast({
                            open: true,
                            type: "error",
                            message: "Select a valid date/time range first",
                          });
                          return;
                        }
                        setSelectedSpaceId(s.id);
                        setModalOpen(true);
                      }}
                    >
                      <Plus size={16} /> Book this space
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <BookingModal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            mode="create"
            spaces={activeSpaces}
            defaultSpaceId={selectedSpaceId}
            defaultStart={
              w ? new Date(`${query.date}T${query.start}`) : undefined
            }
            defaultEnd={w ? new Date(`${query.date}T${query.end}`) : undefined}
            onSaved={() => find()}
          />
        </>
      )}

      <Toast
        open={toast.open}
        type={toast.type}
        message={toast.message}
        onClose={() => setToast((p) => ({ ...p, open: false }))}
      />
    </div>
  );
}
